import { useRef, useState, useCallback, type ReactNode } from 'react';
import type { GeolocateControlInstance } from 'react-map-gl/mapbox';
import {
  GeolocateContext,
  GeolocateStatus,
  type GeolocateContextType,
} from '@/features/geolocateContext';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';

export const GeolocateProvider = ({ children }: { children: ReactNode }) => {
  const geolocateControlRef = useRef<GeolocateControlInstance | null>(null);
  const [status, setStatusState] = useState<GeolocateStatus>(
    GeolocateStatus.DISABLED
  );

  const setGeolocateControlRef = useCallback(
    (ref: GeolocateControlInstance) => {
      geolocateControlRef.current = ref;
    },
    []
  );

  const setStatus = useCallback((newStatus: GeolocateStatus) => {
    setStatusState(newStatus);
    // We only want to trigger the geolocateControl to start tracking when
    // the GeolocateControl is in a disabled state. We have to verify this,
    // because calling trigger() is an on/off operation, and we need to keep it in sync
    // with our multiple status states.
    if (
      newStatus === GeolocateStatus.LOCATING &&
      geolocateControlRef.current?._watchState === 'OFF'
    ) {
      geolocateControlRef.current?.trigger();
      TagManager.dataLayer({
        dataLayer: {
          event: GoogleEvents.geolocatorOn,
        },
      });
    }

    // We only want to trigger the geolocateControl to stop tracking when
    // the GeolocateControl is in a non-disabled state. We have to verify this,
    // because calling trigger() is an on/off operation, and we need to keep it in sync
    // with our multiple status states.
    if (
      newStatus === GeolocateStatus.DISABLED &&
      geolocateControlRef.current?._watchState !== 'OFF'
    ) {
      geolocateControlRef.current?.trigger();
    }
  }, []);

  const value: GeolocateContextType = {
    setGeolocateControlRef,
    status,
    setStatus,
  };

  return (
    <GeolocateContext.Provider value={value}>
      {children}
    </GeolocateContext.Provider>
  );
};
